import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { X, User, Mail, Phone, Calendar, Receipt, MessageSquarePlus, Trash2, Send, Quote, Edit3, Save, ArrowRightLeft, XCircle, History, CheckCircle2 } from "lucide-react";
import api from "../lib/api";

const SESSIONS = {
  tue_am: "Tuesday AM (10–1)",
  tue_pm: "Tuesday PM (2–5)",
  fri_eve: "Friday Evening (6–8:30)",
};

/**
 * Slide-in drawer that shows a student's profile + editable notes log.
 *
 * Props:
 *   - studentId: string | null  — when set, fetches & opens the drawer
 *   - onClose: () => void
 *   - onChanged?: () => void    — parent refreshes its student list after edits
 *
 * Two modes:
 *   - View  (default): read-only fact grid
 *   - Edit  (Edit button in header): every field becomes an input,
 *     PATCH /admin/students/{id} on Save
 * Move-to-term is a separate quick action so admins don't need to enter
 * edit mode just to move a student between terms.
 *
 * On every render we re-fetch /admin/students/{id}/profile so notes stay
 * fresh after add/delete. The backend returns student + lead + invoices +
 * notes + a canonical enquiry_date in a single round-trip.
 */
export default function StudentProfileDrawer({ studentId, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(null);
  const [terms, setTerms] = useState([]);
  const [showMove, setShowMove] = useState(false);
  const [saveBusy, setSaveBusy] = useState(false);

  const load = async () => {
    if (!studentId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/students/${studentId}/profile`);
      setData(data);
      setForm(data.student ? { ...data.student } : null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load student");
      onClose();
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [studentId]);
  // Load terms once — used by the Move-to-term picker and the edit-mode
  // Term dropdown. Cheap enough to fetch every drawer open.
  useEffect(() => {
    if (!studentId) return;
    api.get("/admin/terms").then((r) => setTerms(r.data || [])).catch(() => setTerms([]));
  }, [studentId]);

  const startEdit = () => { setForm({ ...(data?.student || {}) }); setEditing(true); };
  const cancelEdit = () => { setForm({ ...(data?.student || {}) }); setEditing(false); };
  const saveEdits = async () => {
    if (!form) return;
    setSaveBusy(true);
    try {
      // Only send fields the backend accepts on StudentUpdate — extras will
      // be ignored, but sending {..._id, created_at, updated_at} would
      // otherwise waste a round-trip re-writing timestamps.
      const patch = {
        name: form.name,
        email: form.email,
        phone: form.phone,
        session_key: form.session_key,
        term: form.term,
        student_type: form.student_type,
        payment_status: form.payment_status,
        amount_due: form.amount_due != null ? Number(form.amount_due) : undefined,
        amount_paid: form.amount_paid != null ? Number(form.amount_paid) : undefined,
        discount: form.discount != null ? Number(form.discount) : undefined,
        notes: form.notes,
      };
      await api.patch(`/admin/students/${studentId}`, patch);
      toast.success("Student updated");
      setEditing(false);
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaveBusy(false); }
  };
  const moveToTerm = async (newTermLabel) => {
    if (!newTermLabel) return;
    setShowMove(false);
    try {
      await api.post(`/admin/students/${studentId}/move-to-term`, { term: newTermLabel });
      toast.success(`Moved to ${newTermLabel}`);
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Move failed");
    }
  };
  const upd = (k, v) => setForm((f) => ({ ...(f || {}), [k]: v }));

  const addNote = async () => {
    const text = draft.trim();
    if (!text) return;
    setSaving(true);
    try {
      await api.post(`/admin/students/${studentId}/notes`, { text });
      setDraft("");
      await load();
      toast.success("Note added");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't add note");
    } finally { setSaving(false); }
  };

  const removeNote = async (noteId) => {
    if (!confirm("Delete this note?")) return;
    try {
      await api.delete(`/admin/students/${studentId}/notes/${noteId}`);
      await load();
    } catch (e) {
      toast.error("Couldn't delete note");
    }
  };

  if (!studentId) return null;

  const s = data?.student;
  const lead = data?.lead;
  const invoices = data?.invoices || [];
  const notes = data?.notes || [];
  const enrollments = data?.enrollments || [];
  const enquiryDate = data?.enquiry_date;

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="student-profile-drawer">
      <div className="flex-1 bg-black/50" onClick={onClose} aria-label="Close drawer" />
      <aside className="w-full max-w-xl bg-[#FAF6EE] h-full overflow-y-auto shadow-2xl flex flex-col">
        <header className="sticky top-0 bg-[#FAF6EE] border-b border-[#E5E0D8] px-5 py-4 flex items-start justify-between gap-3 z-10">
          <div className="min-w-0">
            <div className="text-[11px] text-[#A685BD] tracking-widest uppercase">Student profile</div>
            {loading ? (
              <div className="h-7 w-48 bg-[#E5E0D8] rounded animate-pulse mt-1" />
            ) : editing ? (
              <input
                value={form?.name || ""}
                onChange={(e) => upd("name", e.target.value)}
                className="mt-1 font-display text-2xl text-[#5D2C8F] bg-white border border-[#E5E0D8] rounded px-2 py-0.5 w-full"
                data-testid="profile-edit-name"
                placeholder="Student name"
              />
            ) : (
              <h2 className="font-display text-2xl text-[#5D2C8F] truncate" data-testid="profile-name">{s?.name || "—"}</h2>
            )}
          </div>
          <div className="flex items-center gap-1 shrink-0">
            {!editing ? (
              <>
                <button onClick={() => setShowMove(true)} className="text-xs text-[#5D2C8F] hover:bg-white rounded px-2 py-1 inline-flex items-center gap-1" data-testid="profile-move-btn" title="Move to another term">
                  <ArrowRightLeft size={13} /> Move term
                </button>
                <button onClick={startEdit} className="text-xs text-[#5D2C8F] hover:bg-white rounded px-2 py-1 inline-flex items-center gap-1" data-testid="profile-edit-btn">
                  <Edit3 size={13} /> Edit
                </button>
              </>
            ) : (
              <>
                <button onClick={cancelEdit} disabled={saveBusy} className="text-xs text-[#A685BD] hover:bg-white rounded px-2 py-1 inline-flex items-center gap-1" data-testid="profile-cancel-edit">
                  <XCircle size={13} /> Cancel
                </button>
                <button onClick={saveEdits} disabled={saveBusy} className="text-xs bg-[#5D2C8F] text-white rounded px-3 py-1 inline-flex items-center gap-1 disabled:opacity-60" data-testid="profile-save-edit">
                  <Save size={13} /> {saveBusy ? "Saving…" : "Save"}
                </button>
              </>
            )}
            <button onClick={onClose} className="text-[#A685BD] p-1.5" aria-label="Close" data-testid="profile-close"><X size={18} /></button>
          </div>
        </header>

        <div className="px-5 py-4 space-y-5 flex-1">
          {/* Quick facts — read-only view */}
          {!editing && (
            <section className="grid grid-cols-2 gap-3 text-sm" data-testid="profile-facts">
              <Fact icon={Mail} label="Email">{s?.email || "—"}</Fact>
              <Fact icon={Phone} label="Phone">{s?.phone || "—"}</Fact>
              <Fact icon={User} label="Session">{SESSIONS[s?.session_key] || s?.session_key}</Fact>
              <Fact icon={Calendar} label="Term">{s?.term || "—"}</Fact>
              <Fact icon={Calendar} label="Date of enquiry">{fmtDate(enquiryDate)}</Fact>
              <Fact icon={Calendar} label="Enrolled on">{fmtDate(s?.created_at)}</Fact>
              <Fact icon={User} label="Type">
                <span className={`text-xs px-2 py-0.5 rounded-full ${s?.student_type === "returner" ? "bg-[#A685BD]/15 text-[#5D2C8F]" : "bg-[#8EC9C7]/20 text-[#1E5F5A]"}`}>
                  {s?.student_type || "new"}
                </span>
              </Fact>
              <Fact icon={Receipt} label="Payment">
                <span className={`text-xs px-2 py-0.5 rounded-full ${s?.payment_status === "paid" ? "bg-[#8EC9C7]/25 text-[#5D2C8F]" : "bg-[#E8C37A]/25 text-[#8F6B1F]"}`}>
                  {s?.payment_status || "unpaid"} · £{(s?.amount_paid || 0).toFixed(0)} / £{(s?.amount_due || 0).toFixed(0)}
                </span>
              </Fact>
              <Fact icon={User} label="Returning next term?">
                {s?.returning_next_term ? (
                  <span className={`text-xs px-2 py-0.5 rounded-full ${s.returning_next_term === "yes" ? "bg-[#8EC9C7]/25 text-[#5D2C8F]" : s.returning_next_term === "maybe" ? "bg-[#E8C37A]/25 text-[#8F6B1F]" : "bg-[#E5E0D8] text-[#6E6074]"}`}>
                    {s.returning_next_term === "yes" ? "Yes ✓" : s.returning_next_term === "maybe" ? "Maybe" : "No"}
                    {s.returning_next_term_label && s.returning_next_term !== "no" ? ` · ${s.returning_next_term_label}` : ""}
                  </span>
                ) : (
                  <span className="text-xs text-[#A685BD]">Not asked yet</span>
                )}
              </Fact>
            </section>
          )}

          {/* Quick action: Mark returning next term — visible in view mode too */}
          {!editing && s && (() => {
            // Pick a sensible upcoming-term label for the tooltip so the
            // admin knows what they're saying yes to. Same fallback chain
            // as the backend endpoint.
            const todayIso = new Date().toISOString().slice(0, 10);
            const upcoming = (() => {
              const sorted = [...(terms || [])].sort((a, b) => (a.start_date || a.created_at || "").localeCompare(b.start_date || b.created_at || ""));
              const activeIdx = sorted.findIndex((t) => t.is_active);
              if (activeIdx >= 0 && activeIdx + 1 < sorted.length) return sorted[activeIdx + 1].label;
              // Fallback: first term with start_date in the future
              const future = sorted.find((t) => t.start_date && t.start_date > todayIso && !t.is_active);
              return future?.label || null;
            })();
            return (
              <div className="bg-white border border-[#E5E0D8] rounded-lg p-3" data-testid="profile-returning-toggles">
                <div className="flex flex-wrap items-center gap-2 mb-1">
                  <span className="text-xs text-[#6E6074] mr-1">Interested in returning{upcoming ? ` for ${upcoming}` : " next term"}?</span>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {[
                    { v: "yes", label: "Yes ✓", color: "bg-[#8EC9C7]" },
                    { v: "maybe", label: "Maybe", color: "bg-[#E8C37A]" },
                    { v: "no", label: "No", color: "bg-[#E5E0D8]" },
                    { v: null, label: "Clear", color: "bg-white border border-[#E5E0D8]" },
                  ].map((opt) => (
                    <button
                      key={opt.label}
                      onClick={async () => {
                        if (opt.v && !upcoming) {
                          toast.error("Set the upcoming term first (Settings → Terms)");
                          return;
                        }
                        try {
                          const { data } = await api.post(
                            `/admin/students/${studentId}/mark-returning`,
                            { response: opt.v, target_term: upcoming },
                          );
                          await load();
                          onChanged?.();
                          if (opt.v === "yes" && data.lead_created) {
                            toast.success(`Added to returners list for ${upcoming} ✓${data.student_copied ? " · seat copied to next term" : ""}`);
                          } else if (opt.v === "maybe" && data.lead_created) {
                            toast.success(`Added as "maybe" for ${upcoming}`);
                          } else if (opt.v === "no") {
                            toast.success("Marked as not returning — we'll skip them on next-term marketing");
                          } else if (opt.v === null) {
                            toast.success("Cleared");
                          } else {
                            toast.success(`Marked ${opt.label.toLowerCase()}`);
                          }
                        } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
                      }}
                      className={`text-xs px-2.5 py-1 rounded-full ${opt.color} ${s.returning_next_term === opt.v ? "ring-2 ring-[#5D2C8F]" : ""} text-[#5D2C8F]`}
                      data-testid={`profile-returning-${opt.v || "clear"}`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
                <p className="text-[10px] text-[#A685BD] mt-1.5">
                  Clicking <strong>Yes</strong> also adds them to the Leads page as a Returner for {upcoming || "the next term"} — same as when a student clicks Yes on our next-term email.
                </p>
              </div>
            );
          })()}

          {/* Editable form */}
          {editing && form && (
            <section className="grid grid-cols-2 gap-3 text-sm bg-white border border-[#E5E0D8] rounded-lg p-4" data-testid="profile-edit-form">
              <EditField label="Email" value={form.email || ""} onChange={(v) => upd("email", v)} testid="profile-edit-email" type="email" />
              <EditField label="Phone" value={form.phone || ""} onChange={(v) => upd("phone", v)} testid="profile-edit-phone" />
              <EditSelect label="Session" value={form.session_key || ""} onChange={(v) => upd("session_key", v)} testid="profile-edit-session" options={Object.entries(SESSIONS).map(([k, v]) => ({ value: k, label: v }))} />
              <EditSelect label="Term" value={form.term || ""} onChange={(v) => upd("term", v)} testid="profile-edit-term" options={termsOptions(terms, form.term)} />
              <EditSelect label="Type" value={form.student_type || "new"} onChange={(v) => upd("student_type", v)} testid="profile-edit-type" options={[
                { value: "new", label: "New" },
                { value: "returner", label: "Returner" },
              ]} />
              <EditSelect label="Payment status" value={form.payment_status || "unpaid"} onChange={(v) => upd("payment_status", v)} testid="profile-edit-payment-status" options={[
                { value: "unpaid", label: "Unpaid" },
                { value: "partial", label: "Partially paid" },
                { value: "paid", label: "Paid in full" },
              ]} />
              <EditField label="Amount due (£)" value={form.amount_due ?? ""} onChange={(v) => upd("amount_due", v)} testid="profile-edit-amount-due" type="number" />
              <EditField label="Amount paid (£)" value={form.amount_paid ?? ""} onChange={(v) => upd("amount_paid", v)} testid="profile-edit-amount-paid" type="number" />
              <EditField label="Discount (£)" value={form.discount ?? ""} onChange={(v) => upd("discount", v)} testid="profile-edit-discount" type="number" />
              <div className="col-span-2">
                <div className="text-[10px] uppercase tracking-wider text-[#A685BD] mb-1">Notes</div>
                <textarea
                  value={form.notes || ""}
                  onChange={(e) => upd("notes", e.target.value)}
                  rows={3}
                  className="w-full bg-white border border-[#E5E0D8] rounded px-2 py-1.5 text-sm"
                  data-testid="profile-edit-notes"
                />
              </div>
            </section>
          )}

          {/* Original enquiry (from the linked lead) */}
          {lead && (
            <section className="admin-surface p-4" data-testid="profile-original-enquiry">
              <div className="flex items-center gap-2 text-[#5D2C8F] mb-2">
                <Quote size={14} />
                <h3 className="font-display text-base">Original enquiry</h3>
              </div>
              {lead.source && <div className="text-[11px] text-[#6E6074] mb-1">Source: {lead.source}</div>}
              {lead.session_preference && <div className="text-xs text-[#6E6074] mb-1">Preferred session: {SESSIONS[lead.session_preference] || lead.session_preference}</div>}
              {lead.notes ? (
                <p className="text-sm text-[#3A2A47] whitespace-pre-wrap leading-relaxed">{lead.notes}</p>
              ) : (
                <p className="text-xs text-[#A685BD] italic">No message was left with the original enquiry.</p>
              )}
            </section>
          )}

          {/* Enrollment history — every term this student has joined.
              Populated by the migration script (Phase 1) and going forward by
              copy-to-term / copy-to-current flows. */}
          {!editing && enrollments.length > 0 && (
            <section data-testid="profile-enrollments">
              <h3 className="font-display text-base text-[#5D2C8F] mb-2 flex items-center gap-2">
                <History size={14} /> Term history ({enrollments.length})
              </h3>
              <ol className="space-y-2">
                {enrollments.map((e, idx) => {
                  const isCurrent = e.term && s?.term && e.term === s.term;
                  const isDeferred = e.status === "deferred";
                  const sessionLabel = SESSIONS[e.session_key] || e.session_key || "—";
                  const typePillClass = e.student_type === "returner"
                    ? "bg-[#A685BD]/15 text-[#5D2C8F]"
                    : "bg-[#8EC9C7]/20 text-[#1E5F5A]";
                  return (
                    <li
                      key={e.id || `${e.term}-${e.session_key}-${idx}`}
                      className={`bg-white border rounded-lg p-3 text-sm ${isCurrent ? "border-[#5D2C8F]/50 ring-1 ring-[#5D2C8F]/20" : isDeferred ? "border-[#E8C37A]/70 bg-[#FDF6E3]/40" : "border-[#E5E0D8]"}`}
                      data-testid={`enrollment-row-${idx}`}
                    >
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <div className="min-w-0">
                          <div className="font-display text-[15px] text-[#5D2C8F] flex items-center gap-2 flex-wrap">
                            <span data-testid={`enrollment-term-${idx}`}>{e.term || "—"}</span>
                            {isCurrent && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#8EC9C7]/25 text-[#1E5F5A] uppercase tracking-wider" data-testid={`enrollment-current-${idx}`}>
                                Current
                              </span>
                            )}
                            {isDeferred && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#E8C37A]/30 text-[#8F6B1F] uppercase tracking-wider" data-testid={`enrollment-deferred-${idx}`}>
                                Deferred{e.deferred_to ? ` → ${e.deferred_to}` : ""}
                              </span>
                            )}
                            <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${typePillClass}`}>
                              {e.student_type || "new"}
                            </span>
                          </div>
                          <div className="text-xs text-[#6E6074] mt-0.5">{sessionLabel}</div>
                        </div>
                        <div className="text-[10px] text-[#A685BD] shrink-0 text-right">
                          {fmtDate(e.joined_at)}
                        </div>
                      </div>
                      <div className="flex items-center flex-wrap gap-x-3 gap-y-1 text-[11px] text-[#6E6074] mt-1">
                        {Number(e.discount) > 0 && (
                          <span>Discount £{Number(e.discount).toFixed(0)}</span>
                        )}
                        {e.welcome_sent && (
                          <span className="inline-flex items-center gap-1 text-[#1E5F5A]">
                            <CheckCircle2 size={11} /> Welcome sent
                            {e.welcome_sent_at ? ` · ${fmtDate(e.welcome_sent_at)}` : ""}
                          </span>
                        )}
                        {e.returning_next_term && (
                          <span className="text-[#5D2C8F]">Marked returning</span>
                        )}
                        {isDeferred && e.deferred_at && (
                          <span className="text-[#8F6B1F]">Deferred on {fmtDate(e.deferred_at)}</span>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ol>
            </section>
          )}

          {/* Notes timeline */}
          <section data-testid="profile-notes">
            <h3 className="font-display text-base text-[#5D2C8F] mb-2 flex items-center gap-2">
              <MessageSquarePlus size={14} /> Internal notes ({notes.length})
            </h3>
            <div className="space-y-2 mb-3">
              {notes.length === 0 && (
                <div className="text-xs text-[#A685BD] italic">No notes yet. Add the first one below — visible only to admins.</div>
              )}
              {notes.map((n) => (
                <article key={n.id} className="bg-white border border-[#E5E0D8] rounded-lg p-3 text-sm" data-testid={`note-${n.id}`}>
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <div className="text-[11px] text-[#6E6074]">
                      <span className="font-medium text-[#5D2C8F]">{n.author_name || n.author_email || "Admin"}</span>
                      <span> · {fmtDateTime(n.created_at)}</span>
                    </div>
                    <button onClick={() => removeNote(n.id)} className="text-[#A685BD] hover:text-[#5D2C8F] p-0.5" aria-label="Delete note" data-testid={`delete-note-${n.id}`}>
                      <Trash2 size={12} />
                    </button>
                  </div>
                  <p className="text-[#3A2A47] whitespace-pre-wrap leading-relaxed">{n.text}</p>
                </article>
              ))}
            </div>
            <div className="bg-white border border-[#E5E0D8] rounded-lg p-3">
              <textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder="Add a note about this student — e.g. allergies, art-supply preferences, last chat…"
                rows={3}
                className="w-full text-sm resize-y outline-none"
                data-testid="profile-note-input"
              />
              <div className="flex items-center justify-between mt-2">
                <span className="text-[10px] text-[#A685BD]">{draft.length}/4000</span>
                <button
                  onClick={addNote}
                  disabled={!draft.trim() || saving}
                  className="sac-btn-primary text-xs disabled:opacity-50"
                  data-testid="profile-note-add"
                >
                  <Send size={12} /> {saving ? "Saving…" : "Add note"}
                </button>
              </div>
            </div>
          </section>

          {/* Invoice history (collapsed list) */}
          {invoices.length > 0 && (
            <section data-testid="profile-invoices">
              <h3 className="font-display text-base text-[#5D2C8F] mb-2 flex items-center gap-2">
                <Receipt size={14} /> Invoices ({invoices.length})
              </h3>
              <ul className="space-y-1 text-xs">
                {invoices.map((inv) => (
                  <li key={inv.id} className="flex items-center justify-between bg-white border border-[#E5E0D8] rounded px-3 py-2">
                    <span className="font-mono text-[#5D2C8F]">{inv.invoice_number}</span>
                    <span className="text-[#6E6074]">£{(inv.total || inv.amount || 0).toFixed(0)}</span>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] ${inv.status === "paid" ? "bg-[#8EC9C7]/25 text-[#5D2C8F]" : "bg-[#E8C37A]/25 text-[#8F6B1F]"}`}>{inv.status}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>
      </aside>

      {/* Move-to-term picker (opened via header button) */}
      {showMove && (
        <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4" onClick={() => setShowMove(false)}>
          <div className="admin-surface w-full max-w-md p-5" onClick={(e) => e.stopPropagation()} data-testid="profile-move-modal">
            <h3 className="font-display text-lg text-[#5D2C8F] mb-1">Move {s?.name} to another term</h3>
            <p className="text-xs text-[#6E6074] mb-3">Currently in <strong>{s?.term || "—"}</strong>. Pick the new term below — invoices stay linked to their original term.</p>
            {terms.length === 0 ? (
              <p className="text-xs text-[#A685BD]">No terms defined yet. Create one in Settings → Terms first.</p>
            ) : (
              <div className="space-y-1.5 max-h-72 overflow-y-auto">
                {terms.map((t) => (
                  <button
                    key={t.id || t.label}
                    onClick={() => moveToTerm(t.label)}
                    disabled={t.label === s?.term}
                    className="w-full text-left bg-white border border-[#E5E0D8] rounded px-3 py-2 text-sm hover:border-[#5D2C8F] disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid={`profile-move-option-${(t.label || "").replace(/\s+/g, "-")}`}
                  >
                    <span className="text-[#5D2C8F] font-medium">{t.label}</span>
                    {t.is_active && <span className="text-[10px] ml-2 px-1.5 py-0.5 rounded-full bg-[#8EC9C7]/20 text-[#5D2C8F]">active</span>}
                    {t.label === s?.term && <span className="text-[10px] ml-2 text-[#6E6074]">(current)</span>}
                  </button>
                ))}
              </div>
            )}
            <div className="flex justify-end mt-4">
              <button onClick={() => setShowMove(false)} className="text-sm text-[#6E6074]" data-testid="profile-move-cancel">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function EditField({ label, value, onChange, testid, type = "text" }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-[#A685BD] mb-1">{label}</div>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} className="w-full bg-white border border-[#E5E0D8] rounded px-2 py-1.5 text-sm" data-testid={testid} />
    </div>
  );
}

function EditSelect({ label, value, onChange, testid, options }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-[#A685BD] mb-1">{label}</div>
      <select value={value} onChange={(e) => onChange(e.target.value)} className="w-full bg-white border border-[#E5E0D8] rounded px-2 py-1.5 text-sm" data-testid={testid}>
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}

// Build the Term dropdown options, always including whatever the student
// currently has (even if it's not in the terms collection — historic data).
function termsOptions(terms, current) {
  const opts = terms.map((t) => ({ value: t.label, label: t.label + (t.is_active ? " (active)" : "") }));
  if (current && !opts.some((o) => o.value === current)) opts.unshift({ value: current, label: `${current} (current)` });
  return opts;
}

function Fact({ icon: Icon, label, children }) {
  return (
    <div className="bg-white border border-[#E5E0D8] rounded-lg p-2.5">
      <div className="text-[10px] uppercase tracking-wider text-[#A685BD] flex items-center gap-1 mb-0.5">
        <Icon size={11} /> {label}
      </div>
      <div className="text-sm text-[#3A2A47] truncate">{children}</div>
    </div>
  );
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return iso; }
}
function fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}
